import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import { ALL_ACCOMMODATIONS, ALL_TASTING_STOPS } from '../lib/data';
import { setPageSeo } from '../utils/seo';

export default function DraftsPage() {
  useEffect(() => {
    setPageSeo({
      title: 'Drafts | Truffle Hunting Oregon',
      description: 'Internal draft staging area for unpublished route research.',
      path: '/drafts',
      robots: 'noindex,nofollow,noarchive',
    });
  }, []);

  const draftStops = ALL_TASTING_STOPS.filter((stop) => !stop.verified);
  const draftStays = ALL_ACCOMMODATIONS.filter((stay) => !stay.verified);

  return (
    <div className="min-h-screen bg-bg font-sans text-ink">
      <div className="bg-amber-500/10 border-b-2 border-amber-500">
        <div className="max-w-5xl mx-auto px-6 md:px-12 py-4 flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-700 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900 leading-relaxed">
            <strong>Internal only.</strong> Draft entries remain unpublished until their sources, access rules, and route fit are verified.
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 md:px-12 py-12 md:py-16 space-y-10">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-ink-light hover:text-accent transition-colors">
          <ArrowLeft size={14} /> Back to route
        </Link>

        <header className="space-y-4">
          <span className="pill-minimal active">Internal / Drafts</span>
          <h1 className="text-5xl md:text-6xl font-serif tracking-tight leading-[1.05]">
            Research drafts <span className="italic font-light text-accent">awaiting verification</span>
          </h1>
          <p className="text-lg text-ink-light max-w-2xl leading-relaxed">
            {draftStops.length} route-stop drafts and {draftStays.length} stay drafts are currently staged.
          </p>
        </header>

        {draftStops.length > 0 ? (
          <section className="space-y-6">
            <div className="max-w-3xl space-y-3">
              <h2 className="text-4xl font-serif tracking-tight">Draft route candidates</h2>
              <p className="text-lg text-ink-light leading-relaxed">
                These are promising enough to keep in the working corridor, but not clean enough to publish as factual route anchors yet.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {draftStops.map((stop) => (
                <article key={stop.id} className="card-minimal p-7 bg-white/80 space-y-4">
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{stop.city}</p>
                    <h3 className="text-3xl font-serif tracking-tight">{stop.name}</h3>
                    <p className="text-sm text-ink-light leading-relaxed">{stop.description}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stop.highlights?.map((item) => (
                      <span key={item} className="pill-minimal">{item}</span>
                    ))}
                  </div>
                  {stop.routeNotes?.length ? (
                    <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                      {stop.routeNotes.map((note) => (
                        <p key={note}>{note}</p>
                      ))}
                    </div>
                  ) : null}
                  {stop.verificationNote ? (
                    <div className="rounded-[18px] bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 leading-relaxed">
                      <strong>Why it stays draft-only:</strong> {stop.verificationNote}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {draftStays.length > 0 ? (
          <section className="space-y-6">
            <div className="max-w-3xl space-y-3">
              <h2 className="text-4xl font-serif tracking-tight">Draft stay candidates</h2>
              <p className="text-lg text-ink-light leading-relaxed">
                These stays exist, but they have not yet earned a place over the stronger first-pass base properties.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {draftStays.map((stay) => (
                <article key={stay.id} className="card-minimal p-7 bg-white/80 space-y-4">
                  <div className="space-y-2">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-accent italic">{stay.city} / {stay.style}</p>
                    <h3 className="text-3xl font-serif tracking-tight">{stay.name}</h3>
                    <p className="text-sm text-ink-light leading-relaxed">{stay.description}</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {stay.amenities.map((item) => (
                      <span key={item} className="pill-minimal">{item}</span>
                    ))}
                  </div>
                  {stay.routeNotes?.length ? (
                    <div className="space-y-2 text-sm text-ink-light leading-relaxed">
                      {stay.routeNotes.map((note) => (
                        <p key={note}>{note}</p>
                      ))}
                    </div>
                  ) : null}
                  {stay.verificationNote ? (
                    <div className="rounded-[18px] bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900 leading-relaxed">
                      <strong>Why it stays draft-only:</strong> {stay.verificationNote}
                    </div>
                  ) : null}
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {draftStops.length === 0 && draftStays.length === 0 ? (
          <div className="card-minimal p-12 bg-white/80 text-center">
            <p className="text-lg text-ink-light italic">
              No drafts are staged yet.
            </p>
          </div>
        ) : null}
      </div>
      <Footer />
    </div>
  );
}
